import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  LEAD_STATUSES,
  QUICK_STATUS_ACTIONS,
  SORT_OPTIONS,
  statusBadgeClass,
  trackDealerLeadEvent,
} from "../../constants/automapDealerLeadConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useLocale } from "../../intl";
import {
  fetchDealerLeads,
  openDealerWhatsapp,
  updateDealerLeadStatus,
} from "../../services/automapDealerLeadApi";
import { fetchDealerPartnerInterest } from "../../services/automapPartnershipApi";

function vehicleLabel(lead) {
  return lead.listingTitle || [lead.vehicleType, lead.budgetRange].filter(Boolean).join(" · ") || "—";
}

export default function DealerLeadsListPage({ preset = "" }) {
  const { dealerId } = useOutletContext();
  const { locale } = useLocale();
  const {
    t,
    formatLeadStatus,
    formatLeadTemperature,
    formatLeadType,
    formatLeadSortLabel,
    formatQuickActionLabel,
    translateError,
  } = useDealerPortalIntl();
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const [pageType, setPageType] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [listingId, setListingId] = useState("");
  const [partnerInterestByListing, setPartnerInterestByListing] = useState({});

  const filters = useMemo(() => {
    const base = { sort, search: search.trim() || undefined };
    if (preset === "hot") base.hot = "true";
    if (preset === "archived") base.archived = "true";
    if (status) base.status = status;
    if (pageType) base.pageType = pageType;
    if (utmCampaign.trim()) base.utmCampaign = utmCampaign.trim();
    if (listingId.trim()) base.listingId = listingId.trim();
    return base;
  }, [preset, sort, search, status, pageType, utmCampaign, listingId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerLeads(dealerId, filters);
      setLeads(data.leads || []);
      setCounts(data.counts || {});
    } catch (err) {
      setError(translateError(err.message, "loadLeads"));
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [dealerId, filters, translateError]);

  useEffect(() => {
    if (!dealerId) return;
    fetchDealerPartnerInterest(dealerId)
      .then((data) => {
        const map = {};
        (data.interests || []).forEach((item) => {
          if (!item.listingId) return;
          if (!map[item.listingId]) map[item.listingId] = [];
          map[item.listingId].push(item.interestBadge);
        });
        setPartnerInterestByListing(map);
      })
      .catch(() => setPartnerInterestByListing({}));
  }, [dealerId]);

  useEffect(() => {
    trackDealerLeadEvent("dealer_lead_inbox_viewed", { dealerId, preset });
  }, [dealerId, preset]);

  useEffect(() => {
    load();
  }, [load]);

  const runQuickAction = async (leadId, action, whatsappUrl) => {
    if (action === "whatsapp" && whatsappUrl) {
      openDealerWhatsapp(whatsappUrl, { dealerId, leadId, onOpened: () => load() });
      trackDealerLeadEvent("dealer_lead_whatsapp_opened", { leadId });
      return;
    }
    try {
      await updateDealerLeadStatus(dealerId, leadId, { status: action });
      trackDealerLeadEvent("dealer_lead_status_changed", { leadId, status: action });
      if (action === "archived") trackDealerLeadEvent("dealer_lead_archived", { leadId });
      await load();
    } catch (err) {
      setError(translateError(err.message, "updateLead"));
    }
  };

  const title =
    preset === "hot"
      ? t("leads.hotTitle")
      : preset === "archived"
        ? t("leads.archivedTitle")
        : t("leads.inboxTitle");

  return (
    <div className="dealer-leads-page">
      <div className="import-page-header">
        <div>
          <h2>{title}</h2>
          <p className="muted">{t("leads.inboxSubtitle")}</p>
        </div>
      </div>

      <div className="lead-count-cards">
        <Link to="/v/automap/dealer/leads" className={!preset && !status ? "active" : ""}>
          {t("leads.allNew")} <strong>{counts.new ?? 0}</strong>
        </Link>
        <Link
          to="/v/automap/dealer/leads/hot"
          className={preset === "hot" ? "active" : ""}
          onClick={() => trackDealerLeadEvent("dealer_hot_lead_clicked")}
        >
          {t("leads.hot")} <strong>{counts.hot ?? 0}</strong>
        </Link>
        <Link to="/v/automap/dealer/leads?status=contacted">
          {t("leads.contacted")} <strong>{counts.contacted ?? 0}</strong>
        </Link>
        <Link to="/v/automap/dealer/leads?status=test_drive_scheduled">
          {t("leads.testDrives")} <strong>{counts.testDrives ?? 0}</strong>
        </Link>
        <Link to="/v/automap/dealer/leads?status=sold">
          {t("leads.sold")} <strong>{counts.sold ?? 0}</strong>
        </Link>
        <Link to="/v/automap/dealer/leads/archivados">
          {t("leads.archived")} <strong>{counts.archived ?? 0}</strong>
        </Link>
      </div>

      <div className="dealer-inventory-toolbar">
        <input
          type="search"
          placeholder={t("leads.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("leads.allStatuses")}</option>
          {LEAD_STATUSES.map((value) => (
            <option key={value} value={value}>
              {formatLeadStatus(value)}
            </option>
          ))}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {formatLeadSortLabel(option.value)}
            </option>
          ))}
        </select>
        <select value={pageType} onChange={(e) => setPageType(e.target.value)}>
          <option value="">{t("leads.sourcePage")}</option>
          {["search_results", "vehicle_detail", "dealer_profile", "buyer_quiz", "recommendation_results", "seo_city", "guide", "checklist"].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <input placeholder={t("leads.campaign")} value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} />
        <input placeholder="Listing ID" value={listingId} onChange={(e) => setListingId(e.target.value)} />
        <Link className="dealer-portal-btn" to="/v/automap/dealer/reportes/leads">
          {t("leads.leadsReport")}
        </Link>
        <button type="button" className="dealer-portal-btn" onClick={load}>
          {t("common.apply")}
        </button>
      </div>

      {error ? <p className="import-error">{error}</p> : null}
      {loading ? <p className="dealer-state-box">{t("leads.loading")}</p> : null}

      {!loading && leads.length === 0 ? (
        <div className="dealer-state-box">
          <p>{t("leads.empty")}</p>
        </div>
      ) : null}

      {!loading && leads.length > 0 ? (
        <div className="import-table-wrap">
          <table className="dealer-inventory-table dealer-leads-table">
            <thead>
              <tr>
                <th>{t("leads.table.buyer")}</th>
                <th>{t("leads.table.whatsapp")}</th>
                <th>{t("leads.table.type")}</th>
                <th>{t("leads.table.status")}</th>
                <th>{t("leads.table.score")}</th>
                <th>{t("leads.table.temperature")}</th>
                <th>{t("leads.table.vehicleInterest")}</th>
                <th>{t("leads.table.city")}</th>
                <th>{t("leads.table.timeline")}</th>
                <th>{t("leads.table.date")}</th>
                <th>{t("leads.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className={`${lead.isUnread ? "lead-row--unread" : ""} ${lead.isHot ? "lead-row--hot" : ""}`}
                >
                  <td>
                    <Link to={`/v/automap/dealer/leads/${lead.id}`}>{lead.buyerName || "—"}</Link>
                    {lead.isHot ? <span className="lead-hot-pill">{t("leads.hotPill")}</span> : null}
                  </td>
                  <td>{lead.buyerWhatsapp || "—"}</td>
                  <td>{formatLeadType(lead.leadType)}</td>
                  <td>
                    <span className={`lead-badge ${statusBadgeClass(lead.status)}`}>
                      {formatLeadStatus(lead.status)}
                    </span>
                  </td>
                  <td>{lead.leadScore ?? "—"}</td>
                  <td>{formatLeadTemperature(lead.leadTemperature)}</td>
                  <td>
                    {vehicleLabel(lead)}
                    {(partnerInterestByListing[lead.listingId] || []).map((badge) => (
                      <span key={badge} className="lead-badge" style={{ display: "block", marginTop: "0.25rem", fontSize: "0.75rem" }}>
                        {badge}
                      </span>
                    ))}
                  </td>
                  <td>{lead.buyerCity || "—"}</td>
                  <td>{lead.purchaseTimeline || "—"}</td>
                  <td>{new Date(lead.createdAt).toLocaleString(locale)}</td>
                  <td className="lead-actions-cell">
                    <Link className="dealer-portal-btn" to={`/v/automap/dealer/leads/${lead.id}`}>
                      {t("common.view")}
                    </Link>
                    {lead.dealerWhatsappUrl ? (
                      <button
                        type="button"
                        className="dealer-portal-btn primary"
                        onClick={() => runQuickAction(lead.id, "whatsapp", lead.dealerWhatsappUrl)}
                      >
                        WA
                      </button>
                    ) : null}
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) runQuickAction(lead.id, e.target.value);
                        e.target.value = "";
                      }}
                    >
                      <option value="">{t("leads.actionPlaceholder")}</option>
                      {QUICK_STATUS_ACTIONS.map((item) => (
                        <option key={item.action} value={item.action}>
                          {formatQuickActionLabel(item.action)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
